import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import { useReveal } from '../../hooks/useReveal';
import { useTranslation } from 'react-i18next';

// ... existing code ...

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const ref = useReveal();
  const { t } = useTranslation();
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = (data.get('message') as string) || '';
    if (message.length > 500) return;
    setFormState('submitting');
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== 'message' || typeof value === 'string') {
        body.append(key, value as string);
      }
    });
    try {
      await fetch('https://readdy.ai/api/form/d6vllp5t4s7j0tv5ih8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFormState('success');
      form.reset();
      setCharCount(0);
    } catch {
      setFormState('error');
    }
  };

  return (
    <Layout>
      <title>{t('page.title.contact')}</title>
      <meta name="description" content="Reach our trade specialists in Seoul or Ho Chi Minh City. Get a free Korea-Vietnam trade assessment for import/export logistics, customs compliance, and market entry." />

      {/* Hero */}
      <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-16 bg-navy">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">{t('contactPage.eyebrow')}</p>
          <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4">
            {t('contactPage.heading1')}
          </h1>
          <p className="text-white/60 font-sans text-base lg:text-lg max-w-xl">{t('contactPage.heroDesc')}</p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section ref={ref} className="bg-white pt-12 lg:pt-16 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left — Offices */}
            <div className="reveal-left bg-navy rounded-t-2xl lg:rounded-tl-2xl lg:rounded-bl-2xl lg:rounded-tr-none p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <h2 className="font-serif text-white text-2xl lg:text-3xl font-bold mb-6 lg:mb-8">{t('contactPage.offices.heading')}</h2>

              {/* Seoul Office */}
              <div className="mb-8 pb-8 border-b border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-silver text-sm" />
                  </div>
                  <h3 className="font-serif text-silver text-lg lg:text-xl font-semibold">{t('contactPage.offices.seoul')}</h3>
                </div>
                <div className="space-y-2 pl-11">
                  <p className="text-white/60 text-sm font-sans">경기도 고양시 일산동구</p>
                  <p className="text-white/60 text-sm font-sans">중앙로 1080 (백석동)</p>
                  <p className="text-white/60 text-sm font-sans">남정골드프라자 402-638호</p>
                  <div className="pt-2 space-y-1">
                    <p className="text-white/50 text-xs font-sans flex items-center gap-2"><i className="ri-phone-line text-silver" />+82-70-7174-3580</p>
                    <p className="text-white/50 text-xs font-sans flex items-center gap-2"><i className="ri-mail-line text-silver" />chan@monaru.co.kr</p>
                  </div>
                </div>
              </div>

              {/* HCMC Office */}
              <div className="mb-8 pb-8 border-b border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-silver text-sm" />
                  </div>
                  <h3 className="font-serif text-silver text-lg lg:text-xl font-semibold">{t('contactPage.offices.hcmc')}</h3>
                </div>
                <div className="space-y-2 pl-11">
                  <p className="text-white/60 text-sm font-sans">64 Street No.2,</p>
                  <p className="text-white/60 text-sm font-sans">Tan Hung, District 7,</p>
                  <p className="text-white/60 text-sm font-sans">District 1, Ho Chi Minh City, Vietnam</p>
                  <div className="pt-2 space-y-1">
                    <p className="text-white/50 text-xs font-sans flex items-center gap-2"><i className="ri-phone-line text-silver" />+82-70-7174-3580</p>
                    <p className="text-white/50 text-xs font-sans flex items-center gap-2"><i className="ri-mail-line text-silver" />chan@monaru.co.kr</p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                    <i className="ri-time-line text-silver text-sm" />
                  </div>
                  <h3 className="font-serif text-silver text-lg lg:text-xl font-semibold">{t('contactPage.offices.hours')}</h3>
                </div>
                <div className="pl-11 space-y-1">
                  <p className="text-white/60 text-sm font-sans">{t('contactPage.offices.hoursMF')}</p>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="reveal-right bg-silver-light p-8 lg:p-12 rounded-b-2xl lg:rounded-tr-2xl lg:rounded-br-2xl lg:rounded-bl-none">
              <h2 className="font-serif text-navy text-2xl lg:text-3xl font-bold mb-2">{t('contactPage.form.heading')}</h2>
              <p className="text-navy/55 text-sm font-sans mb-6 lg:mb-8">{t('contactPage.form.sub')}</p>

              {formState === 'success' ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-navy rounded-full mb-4">
                    <i className="ri-check-line text-silver text-2xl" />
                  </div>
                  <h3 className="font-serif text-navy text-2xl font-bold mb-2">{t('contactPage.form.successTitle')}</h3>
                  <p className="text-navy/60 text-sm font-sans">{t('contactPage.form.successDesc')}</p>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-sans font-medium text-navy/70 mb-1.5" htmlFor="name">
                        {t('contactPage.form.name')} <span className="text-red-400">*</span>
                      </label>
                      <input id="name" name="name" type="text" required placeholder="Lee Sung-jin"
                        className="w-full h-12 px-4 bg-white border border-navy/10 rounded-lg text-navy text-sm font-sans placeholder:text-navy/30 focus:outline-none focus:border-navy transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs font-sans font-medium text-navy/70 mb-1.5" htmlFor="email">
                        {t('contactPage.form.email')} <span className="text-red-400">*</span>
                      </label>
                      <input id="email" name="email" type="email" required placeholder="lee@company.com"
                        className="w-full h-12 px-4 bg-white border border-navy/10 rounded-lg text-navy text-sm font-sans placeholder:text-navy/30 focus:outline-none focus:border-navy transition-colors duration-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-sans font-medium text-navy/70 mb-1.5" htmlFor="company">
                        {t('contactPage.form.company')}
                      </label>
                      <input id="company" name="company" type="text" placeholder="Company Name"
                        className="w-full h-12 px-4 bg-white border border-navy/10 rounded-lg text-navy text-sm font-sans placeholder:text-navy/30 focus:outline-none focus:border-navy transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs font-sans font-medium text-navy/70 mb-1.5" htmlFor="service">
                        {t('contactPage.form.service')}
                      </label>
                      <select id="service" name="service"
                        className="w-full h-12 px-4 bg-white border border-navy/10 rounded-lg text-navy text-sm font-sans focus:outline-none focus:border-navy transition-colors duration-200 cursor-pointer">
                        <option value="">{t('contactPage.form.serviceSelect')}</option>
                        <option value="korea-export">{t('contactPage.form.service.koreanExport')}</option>
                        <option value="vietnam-dist">{t('contactPage.form.service.vietnamDist')}</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sans font-medium text-navy/70 mb-1.5" htmlFor="message">
                      {t('contactPage.form.message')} <span className="text-red-400">*</span>
                    </label>
                    <textarea id="message" name="message" required rows={5} maxLength={500}
                      placeholder={t('contactPage.form.messagePlaceholder')}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 bg-white border border-navy/10 rounded-lg text-navy text-sm font-sans placeholder:text-navy/30 focus:outline-none focus:border-navy transition-colors duration-200 resize-none" />
                    <p className={`text-xs font-sans mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-navy/30'}`}>{charCount}/500</p>
                  </div>
                  {formState === 'error' && (
                    <p className="text-red-500 text-sm font-sans">{t('contactPage.form.error')}</p>
                  )}
                  <button type="submit" disabled={formState === 'submitting'}
                    className="w-full h-14 bg-navy text-white text-sm font-sans font-semibold rounded-lg hover:bg-navy-light transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                    {formState === 'submitting' ? t('contactPage.form.submitting') : t('contactPage.form.submit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl overflow-hidden h-72">
            <iframe
              title="Seoul Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3159.1576252051645!2d126.78246677640864!3d37.645498119595395!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357c9aca79247a39%3A0xed6e4369be46310c!2z64Ko7KCV6rOo65Oc7ZSE65287J6Q!5e0!3m2!1sen!2skr!4v1774093804720!5m2!1sen!2skr"
              width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}
